import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import {
  TrendingUp,
  AlertCircle,
  DollarSign,
  Scale,
  FileText
} from 'lucide-react';
import type { Prognostico, TipoCenario } from '../../tipos/tiposPeticao';

/**
 * COMPONENTE: Gráfico de Prognóstico de Petição Inicial
 * 
 * PROPÓSITO:
 * - Visualizar probabilidades de sucesso via gráfico de pizza
 * - Exibir cenários (melhor caso, esperado, pior caso) em tabela
 * - Mostrar recomendação estratégica baseada na análise
 * 
 * DEPENDÊNCIAS:
 * - Recharts (biblioteca de gráficos)
 * - Lucide React (ícones)
 * - ShadcnUI (componentes base)
 */

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface ComponenteGraficoPrognosticoProps {
  dados: Prognostico;
  className?: string;
  perspectiva?: 'autor' | 'reu';
}

// ============================================================================
// CONFIGURAÇÕES DE CORES E ESTILOS
// ============================================================================

const CORES_CENARIOS: Record<TipoCenario, string> = {
  vitoria_total: '#10b981', // green-500
  vitoria_parcial: '#3b82f6', // blue-500
  acordo: '#a855f7', // purple-500
  derrota: '#ef4444', // red-500
  derrota_com_condenacao: '#b91c1c', // red-700
};

// ============================================================================
// FUNÇÕES AUXILIARES
// ============================================================================

/**
 * Formata valor monetário para Real Brasileiro
 */
const formatarMoeda = (valor?: number): string => {
  if (!valor && valor !== 0) return 'N/A';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(valor);
};

/**
 * Formata tempo em meses para string legível
 */
const formatarTempo = (meses?: number): string => {
  if (!meses && meses !== 0) return 'N/A';
  if (meses < 12) return `${meses} ${meses === 1 ? 'mês' : 'meses'}`;
  const anos = Math.floor(meses / 12);
  const mesesRestantes = meses % 12;
  if (mesesRestantes === 0) return `${anos} ${anos === 1 ? 'ano' : 'anos'}`;
  return `${anos}a ${mesesRestantes}m`;
};

/**
 * Traduz tipo de cenário para label PT-BR
 */
const obterLabelCenario = (tipo: TipoCenario, perspectiva: 'autor' | 'reu' = 'reu'): string => {
  const sufixo = perspectiva === 'autor' ? '(Autor)' : '(Defesa)';

  const labels: Record<TipoCenario, string> = {
    vitoria_total: `Vitória Total ${sufixo}`,
    vitoria_parcial: `Vitória Parcial ${sufixo}`,
    acordo: 'Acordo',
    derrota: `Derrota ${sufixo}`,
    derrota_com_condenacao: `Derrota com Condenação ${sufixo}`,
  };
  return labels[tipo];
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export const ComponenteGraficoPrognostico: React.FC<ComponenteGraficoPrognosticoProps> = ({
  dados,
  className = '',
  perspectiva = 'reu',
}) => {
  // --------------------------------------------------------------------------
  // PREPARAÇÃO DOS DATOS PARA O GRÁFICO
  // --------------------------------------------------------------------------

  const dadosGrafico = Array.isArray(dados.cenarios) ? dados.cenarios.map(cenario => ({
    nome: obterLabelCenario(cenario.tipo, perspectiva),
    valor: cenario.probabilidade_percentual,
    cor: CORES_CENARIOS[cenario.tipo],
  })) : [];

  // --------------------------------------------------------------------------
  // TOOLTIP CUSTOMIZADO
  // --------------------------------------------------------------------------

  interface TooltipPayload {
    name: string;
    value: number;
    color: string;
  }

  interface TooltipProps {
    active?: boolean;
    payload?: TooltipPayload[];
  }

  const TooltipCustomizado = ({ active, payload }: TooltipProps) => {
    if (active && payload && payload.length) {
      return (
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-lg">
          <p className="font-semibold text-slate-900">{payload[0].name}</p>
          <p className="text-sm text-slate-600">
            {payload[0].value.toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  // --------------------------------------------------------------------------
  // RENDERIZAÇÃO
  // --------------------------------------------------------------------------

  const textoExplicativo = perspectiva === 'autor'
    ? `Este prognóstico analisa as chances do AUTOR (reclamante).\nVitória = procedência dos pedidos.\nDerrota = improcedência dos pedidos.`
    : `Este prognóstico analisa as chances da DEFESA (reclamada).\nVitória = processo improcedente, defesa vence.\nDerrota = processo procedente, condenação ao pagamento.`;

  const textoPerspectiva = perspectiva === 'autor'
    ? (
      <>
        <strong>Perspectiva da Análise:</strong> Todos os cenários são analisados
        do ponto de vista do <strong>AUTOR (reclamante)</strong>. Vitória significa
        que o autor vence o processo (procedência dos pedidos).
      </>
    )
    : (
      <>
        <strong>Perspectiva da Análise:</strong> Todos os cenários são analisados
        do ponto de vista da <strong>DEFESA (reclamada)</strong>. Vitória significa
        que a reclamada vence o processo (improcedência dos pedidos).
      </>
    );

  return (
    <div className={`space-y-6 ${className}`}>
      {/* CARD PRINCIPAL: GRÁFICO DE PROBABILIDADE */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Scale className="size-5 text-blue-600" />
            <span>Prognóstico de Sucesso</span>
            {/* Ícone de informação com tooltip explicativo (title usado para tooltip nativo) */}
            <span
              className="ml-2 text-sm text-slate-400"
              title={textoExplicativo}
            >
              ⓘ
            </span>
          </h3>
        </div>
        <div className="p-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* GRÁFICO DE PIZZA */}
            <div className="relative flex flex-col items-center justify-center">
              {dadosGrafico.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={dadosGrafico}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="valor"
                    >
                      {dadosGrafico.map((entrada, index) => (
                        <Cell key={`cell-${index}`} fill={entrada.cor} />
                      ))}
                    </Pie>
                    <Tooltip content={<TooltipCustomizado />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-[250px] w-full items-center justify-center text-slate-400">
                  Sem dados para o gráfico
                </div>
              )}
              {/* LABEL CENTRAL (sobreposto) */}
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
                <div className="text-lg font-bold text-slate-900">
                  Cenários
                </div>
                <div className="text-xs text-slate-500">
                  Visão Detalhada
                </div>
              </div>
            </div>

            {/* INDICADORES LATERAIS */}
            <div className="flex flex-col justify-center space-y-4">
              {/* Cenário mais provável */}
              <div className="flex items-center gap-3">
                <TrendingUp className="size-5 text-blue-600" />
                <div>
                  <p className="text-sm text-slate-600">Cenário Mais Provável</p>
                  <p className="text-base font-semibold text-slate-900">
                    {dados.cenario_mais_provavel}
                  </p>
                </div>
              </div>

              {/* Fatores Críticos (Badge Count) */}
              {dados.fatores_criticos && dados.fatores_criticos.length > 0 && (
                <div className="flex items-center gap-3">
                  <FileText className="size-5 text-amber-600" />
                  <div>
                    <p className="text-sm text-slate-600">Fatores Críticos</p>
                    <p className="text-lg font-semibold text-slate-900">
                      {dados.fatores_criticos.length} identificados
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CARD: CENÁRIOS DETALHADOS */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <DollarSign className="size-5 text-green-600" />
            Cenários de Resultado
          </h3>
        </div>
        <div className="p-6">
          {/* Nota explicativa sobre a perspectiva da análise (reclamada/defesa) */}
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
            <p className="text-sm text-blue-800">
              {textoPerspectiva}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                    Cenário
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">
                    Probabilidade
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">
                    Valor Estimado
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">
                    Prazo Estimado
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                    Descrição
                  </th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(dados.cenarios) && dados.cenarios.map((cenario, index) => (
                  <tr
                    key={index}
                    className="border-b border-slate-100 transition-colors hover:bg-slate-50"
                  >
                    {/* Tipo de Cenário */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="size-3 rounded-full"
                          style={{ backgroundColor: CORES_CENARIOS[cenario.tipo] }}
                        />
                        <span className="font-medium text-slate-900">
                          {obterLabelCenario(cenario.tipo, perspectiva)}
                        </span>
                      </div>
                    </td>

                    {/* Probabilidade */}
                    <td className="px-4 py-3 text-center">
                      <span
                        className="inline-block rounded-md border px-2.5 py-0.5 font-mono text-sm"
                        style={{ borderColor: CORES_CENARIOS[cenario.tipo] }}
                      >
                        {cenario.probabilidade_percentual.toFixed(0)}%
                      </span>
                    </td>

                    {/* Valor Estimado (Receber/Pagar) */}
                    <td className="px-4 py-3 text-right font-mono text-slate-900">
                      {cenario.valores_estimados ? (
                        <div className="flex flex-col items-end text-xs">
                          {/* Economia = receber (bom para defesa) -> verde */}
                          {cenario.valores_estimados.receber > 0 && (
                            <span className="text-green-600">
                              {formatarMoeda(cenario.valores_estimados.receber)}
                            </span>
                          )}

                          {/* Condenação = pagar (ruim para defesa) -> vermelho */}
                          {cenario.valores_estimados.pagar > 0 && (
                            <span className="text-red-600">
                              {formatarMoeda(cenario.valores_estimados.pagar)}
                            </span>
                          )}
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>

                    {/* Prazo Estimado */}
                    <td className="px-4 py-3 text-center text-slate-700">
                      {formatarTempo(cenario.tempo_estimado_meses)}
                    </td>

                    {/* Descrição */}
                    <td className="max-w-xs px-4 py-3 text-sm text-slate-600">
                      {cenario.descricao}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* CARD: RECOMENDAÇÃO ESTRATÉGICA */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start gap-3">
          <Scale className="mt-0.5 size-5 shrink-0 text-blue-600" />
          <div className="flex-1">
            <p className="mb-2 font-semibold text-blue-900">
              Recomendação Estratégica:
            </p>
            <p className="text-sm leading-relaxed text-blue-800">
              {dados.recomendacao_geral}
            </p>
          </div>
        </div>
      </div>

      {/* CARD: FATORES CRÍTICOS */}
      {dados.fatores_criticos && dados.fatores_criticos.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-6 py-4">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <AlertCircle className="size-5 text-amber-600" />
              Fatores Críticos para o Sucesso
            </h3>
          </div>
          <div className="p-6">
            <ul className="space-y-2">
              {dados.fatores_criticos.map((fator, index) => (
                <li key={index} className="flex items-start gap-2">
                  <div className="mt-2 size-1.5 shrink-0 rounded-full bg-amber-500" />
                  <span className="text-sm text-slate-700">{fator}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComponenteGraficoPrognostico;